import {
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  Delete,
  Info as InfoIcon,
  LocationOn as LocationOnIcon,
  PostAdd as PostAddIcon,
  Settings as SettingsIcon,
  Tag as TagIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import AutoPostApiHandler from './AutoPostApiHandler';
import ContentFocusSelector from './content-focus-selector.component';

const focusOptions = [
  { label: 'Articles', value: 'articles', iconKey: 'article' },
  { label: 'Education', value: 'education', iconKey: 'education' },
  { label: 'Trending', value: 'trending', iconKey: 'trending' },
  { label: 'Shopping', value: 'shopping', iconKey: 'shopping' },
  { label: 'Promotions', value: 'promotions', iconKey: 'promotions' },
];

const FullyAutomaticTab = ({
  autoPostBrandDetails,
  fetchAutoPostBrandDetails,
  imageModels,
  selectedImageModel,
  setSelectedImageModel,
}) => {
  const { t: translate } = useTranslation();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage('is_portal_admin');
  const [contentLanguages, setContentLanguages] = useState(['English']);
  const [languageInput, setLanguageInput] = useState('');
  const { showToast } = useToaster();
  const [locationBasedEnabled, setLocationBasedEnabled] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationInput, setLocationInput] = useState('');
  const isCustomDomain = CommonHelper.CustomDomain();
  const contentOptions = [
    { label: 'Text', info: '(Approx 25 text tokens) / post' },
    { label: 'Image', info: '(Approx 1 image  + 25 Text tokens) / post' },
    { label: 'Carousel', info: '(Approx 3-4 image  + 25 Text tokens) / post' },
    { label: 'Link', info: '(Upcoming)', disabled: true },
    { label: 'Short Videos', info: '(Upcoming)', disabled: true },
  ];
  const [semiPostsPerWeek, setSemiPostsPerWeek] = useState(3);
  const [postingHours, setPostingHours] = useState([9, 20]);
  const [contentTypes, setContentTypes] = useState(['Text']);
  const [customPostingHoursEnabled, setCustomPostingHoursEnabled] = useState(false);
  const [aiDeterminedHours, setAiDeterminedHours] = useState(true);
  const [includeTrending, setIncludeTrending] = useState(false);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [prompt, setPrompt] = useState([]);
  const [promptInput, setPromptInput] = useState('');
  const [useBrandDetails, setUseBrandDetails] = useState(true);
  const [loading, setLoading] = useState(false);
  const loadingMessages = [
    'Analyzing your audience...',
    'Crafting engaging content...',
    'Optimizing posting schedule...',
    'Adding trending topics...',
    'Generating hashtags...',
    'Finalizing your posts...',
  ];
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const loadingIntervalRef = useRef(null);
  const [websiteUrls, setWebsiteUrls] = useState([]);
  const [websiteInput, setWebsiteInput] = useState('');
  const [contentFocus, setContentFocus] = useState(focusOptions);
  const [isEditing, setIsEditing] = useState(false);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [isPaused, setIsPaused] = useState(autoPostBrandDetails?.is_autopost_paused || false);
  const [confirmation, setConfirmation] = useState({ open: false, action: null });

  useEffect(() => {
    if (loading) {
      setLoadingMsgIdx(0);
      loadingIntervalRef.current = setInterval(() => {
        setLoadingMsgIdx((prev) => (prev + 1) % loadingMessages.length);
      }, 1800);
    } else {
      clearInterval(loadingIntervalRef.current);
      setLoadingMsgIdx(0);
    }
    return () => clearInterval(loadingIntervalRef.current);
  }, [loading]);

  useEffect(() => {
    setIsPaused(autoPostBrandDetails?.is_autopost_paused || false);
  }, [autoPostBrandDetails?.is_autopost_paused]);

  useEffect(() => {
    if (autoPostBrandDetails?.is_autopost_setup_complete && autoPostBrandDetails.auto_post_details) {
      const d = autoPostBrandDetails.auto_post_details;
      setSemiPostsPerWeek(d.posts_per_week || 3);
      setPostingHours(d.posting_hours || [9, 20]);
      setAiDeterminedHours(typeof d.ai_determined_hours === 'boolean' ? d.ai_determined_hours : true);
      setCustomPostingHoursEnabled(!!d.custom_posting_hours_enabled);
      setContentTypes(d.content_types || ['Text']);
      setContentLanguages(d.content_languages || ['English']);
      setIncludeTrending(!!d.include_trending);
      setIncludeHashtags(typeof d.include_hashtags === 'boolean' ? d.include_hashtags : true);
      setPrompt(Array.isArray(d.prompt) ? d.prompt : []);
      setUseBrandDetails(typeof d.use_brand_details === 'boolean' ? d.use_brand_details : true);
      setLocationBasedEnabled(!!d.location_based_enabled);
      setLocations(Array.isArray(d.locations) ? d.locations : []);
      setWebsiteUrls(Array.isArray(d.website_urls) ? d.website_urls : []);
      const model = imageModels.find((m) => m.model === d.model);
      model && setSelectedImageModel(model);

      setContentFocus(
        Array.isArray(d.type_of_contents)
          ? focusOptions.filter((opt) => d.type_of_contents.includes(opt.value))
          : focusOptions
      );
    }
  }, [autoPostBrandDetails?.is_autopost_setup_complete, autoPostBrandDetails?.auto_post_details]);

  const successCallback = (data, message) => {
    message && showToast(message, 's');
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
  };

  const handleConfirm = () => {
    if (confirmation.action) confirmation.action();
    setConfirmation({ open: false, action: null });
  };
  const handleCancel = () => setConfirmation({ open: false, action: null });

  const confirmDialog = (title, content, action) => {
    setConfirmation({
      open: true,
      action,
      title,
      content,
    });
  };

  return (
    <AutoPostApiHandler
      showToast={showToast}
      fetchAutoPostBrandDetails={fetchAutoPostBrandDetails}
      onLoadingChange={(val) => setLoading(val)}
    >
      {({ doStartAuto, doPauseAutoPost, doResumeAutoPost, doUpdateAutoPost, doDeleteAutoPost }) => {
        // All handler functions must be defined inside this render function to access API methods
        const handleStartAuto = () => {
          if (!selectedImageModel) {
            showToast('Please select an image model before starting auto post.', 'e');
            return;
          }
          if (!semiPostsPerWeek || semiPostsPerWeek === '' || isNaN(semiPostsPerWeek)) {
            showToast('Please enter the number of posts per week.', 'e');
            return;
          }
          confirmDialog(
            'Start Auto Post?',
            'Are you sure you want to start auto post with the current settings?',
            () => {
              let finalPrompt = [...prompt];
              if (promptInput.trim()) {
                const newPrompt = promptInput.trim();
                if (!finalPrompt.includes(newPrompt)) {
                  finalPrompt = [...finalPrompt, newPrompt];
                }
                setPrompt(finalPrompt);
                setPromptInput('');
              }
              let finalWebsiteUrls = websiteUrls;
              if (websiteInput.trim()) {
                const newUrls = websiteInput
                  .split(',')
                  .map((k) => k.trim())
                  .filter((k) => k && isValidHttpsUrl(k) && !websiteUrls.includes(k));
                finalWebsiteUrls = [...websiteUrls, ...newUrls];
                setWebsiteUrls(finalWebsiteUrls);
                setWebsiteInput('');
              }
              doStartAuto({
                prompt: finalPrompt,
                websiteUrls: finalWebsiteUrls,
                locationInput,
                locations,
                posts_per_week: semiPostsPerWeek,
                posting_hours: postingHours,
                ai_determined_hours: aiDeterminedHours,
                custom_posting_hours_enabled: customPostingHoursEnabled,
                content_types: contentTypes,
                content_languages: contentLanguages,
                include_trending: includeTrending,
                include_hashtags: includeHashtags,
                use_brand_details: useBrandDetails,
                location_based_enabled: locationBasedEnabled,
                type_of_contents: contentFocus.map((focus) => focus.value),
                model: selectedImageModel.model,
                model_provider: selectedImageModel.model_provider,
              });
            }
          );
        };
        const handlePauseAutoPost = () => {
          confirmDialog('Pause Auto Post?', 'Are you sure you want to pause auto post?', () => doPauseAutoPost());
        };
        const handleResumeAutoPost = () => {
          confirmDialog('Resume Auto Post?', 'Are you sure you want to resume auto post?', () => doResumeAutoPost());
        };
        const handleEditClick = () => {
          confirmDialog('Edit Auto Post?', 'Are you sure you want to edit auto post settings?', () => {
            setIsEditing(true);
            setIsInputDisabled(true);
          });
        };
        const handleUpdateClick = () => {
          if (!semiPostsPerWeek || semiPostsPerWeek === '' || isNaN(semiPostsPerWeek)) {
            showToast('Please enter the number of posts per week.', 'e');
            return;
          }
          confirmDialog(
            'Update Auto Post?',
            'Are you sure you want to update auto post with the current settings?',
            () => {
              setIsEditing(false);
              setIsInputDisabled(false);
              let finalPrompt = [...prompt];
              if (promptInput.trim()) {
                const newPrompt = promptInput.trim();
                if (!finalPrompt.includes(newPrompt)) {
                  finalPrompt = [...finalPrompt, newPrompt];
                }
                setPrompt(finalPrompt);
                setPromptInput('');
              }
              let finalWebsiteUrls = websiteUrls;
              if (websiteInput.trim()) {
                const newUrls = websiteInput
                  .split(',')
                  .map((k) => k.trim())
                  .filter((k) => k && isValidHttpsUrl(k) && !websiteUrls.includes(k));
                finalWebsiteUrls = [...websiteUrls, ...newUrls];
                setWebsiteUrls(finalWebsiteUrls);
                setWebsiteInput('');
              }
              let finalLocations = locations;
              if (locationInput.trim()) {
                const newLocations = locationInput
                  .split(',')
                  .map((loc) => loc.trim())
                  .filter((loc) => loc && !locations.includes(loc));
                finalLocations = [...locations, ...newLocations];
                setLocations(finalLocations);
                setLocationInput('');
              }
              const payload = {
                posts_per_week: semiPostsPerWeek,
                posting_hours: postingHours,
                ai_determined_hours: aiDeterminedHours,
                custom_posting_hours_enabled: customPostingHoursEnabled,
                content_types: contentTypes,
                content_languages: contentLanguages,
                include_trending: includeTrending,
                include_hashtags: includeHashtags,
                prompt: finalPrompt,
                use_brand_details: useBrandDetails,
                location_based_enabled: locationBasedEnabled,
                locations: finalLocations,
                website_urls: finalWebsiteUrls,
                type_of_contents: contentFocus.map((focus) => focus.value),
                model: selectedImageModel.model,
                model_provider: selectedImageModel.model_provider,
              };
              doUpdateAutoPost(payload);
            }
          );
        };
        const handleDeleteClick = () => {
          confirmDialog(
            'Delete Auto Post?',
            'Are you sure you want to delete this auto post schedule? This action cannot be undone.',
            () => doDeleteAutoPost()
          );
        };

        const handleContentTypeChange = (event) => {
          const value = event.target.value;
          setContentTypes(typeof value === 'string' ? value.split(',') : value);
        };

        const handlePromptAdd = (event) => {
          if (event.key === 'Enter' && promptInput.trim()) {
            const newPrompt = promptInput.trim();
            if (!prompt.includes(newPrompt)) {
              setPrompt([...prompt, newPrompt]);
            }
            setPromptInput('');
          }
        };

        const handlePromptDelete = (deletedPrompt) => () => {
          setPrompt(prompt.filter((p) => p !== deletedPrompt));
        };

        const handleLanguageAdd = (event) => {
          if (event.key === 'Enter' && languageInput.trim()) {
            event.preventDefault();
            const newLanguages = languageInput
              .split(',')
              .map((k) => k.trim())
              .filter((k) => k && !contentLanguages.includes(k));
            setContentLanguages([...contentLanguages, ...newLanguages]);
            setLanguageInput('');
          }
        };

        const handleLanguageDelete = (deletedLanguage) => () => {
          setContentLanguages(contentLanguages.filter((lang) => lang !== deletedLanguage));
        };

        const handleLocationToggle = () => {
          setLocationBasedEnabled((prev) => !prev);
          if (locationBasedEnabled) {
            setLocations([]);
            setLocationInput('');
          }
        };

        const handleLocationAdd = (event) => {
          if (event.key === 'Enter' && locationInput.trim()) {
            event.preventDefault();
            const newLocations = locationInput
              .split(',')
              .map((loc) => loc.trim())
              .filter((loc) => loc && !locations.includes(loc));
            setLocations([...locations, ...newLocations]);
            setLocationInput('');
          }
        };

        const handleLocationDelete = (deletedLocation) => () => {
          setLocations(locations.filter((loc) => loc !== deletedLocation));
        };

        const handleAiHoursToggle = () => {
          setAiDeterminedHours((prev) => !prev);
          if (aiDeterminedHours) {
            setPostingHours([9, 20]);
          }
        };

        const isValidHttpsUrl = (url) => {
          try {
            const u = new URL(url);
            return u.protocol === 'https:';
          } catch {
            return false;
          }
        };

        const contentDisabled = autoPostBrandDetails?.is_autopost_setup_complete && !isEditing;

        return (
          <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, gap: 2 }}>
              <Button
                variant="contained"
                color={
                  !autoPostBrandDetails?.is_autopost_setup_complete
                    ? 'primary'
                    : autoPostBrandDetails?.is_autopost_setup_complete
                    ? 'warning'
                    : 'primary'
                }
                size="large"
                onClick={
                  !autoPostBrandDetails?.is_autopost_setup_complete
                    ? handleStartAuto
                    : isPaused
                    ? handleResumeAutoPost
                    : handlePauseAutoPost
                }
                sx={{
                  height: 48,
                  minWidth: 250,
                  fontSize: '1.1rem',
                  borderRadius: 2,
                  boxShadow: 2,
                  background: !autoPostBrandDetails?.is_autopost_setup_complete
                    ? (theme) => theme.palette.primary.main
                    : autoPostBrandDetails?.is_autopost_setup_complete
                    ? 'linear-gradient(90deg, #ffb347 0%, #ffcc80 100%)'
                    : undefined,
                  color: !autoPostBrandDetails?.is_autopost_setup_complete
                    ? '#fff'
                    : autoPostBrandDetails?.is_autopost_setup_complete
                    ? '#5d4037'
                    : undefined,
                  '&:hover': {
                    background: !autoPostBrandDetails?.is_autopost_setup_complete
                      ? (theme) => theme.palette.primary.dark
                      : autoPostBrandDetails?.is_autopost_setup_complete
                      ? 'linear-gradient(90deg, #ffa726 0%, #ffd699 100%)'
                      : undefined,
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
                startIcon={<AutoAwesomeIcon />}
                disabled={loading}
              >
                {!autoPostBrandDetails?.is_autopost_setup_complete ? (
                  translate('auto_post.start_auto_post', 'Start Auto Post')
                ) : isPaused ? (
                  translate('auto_post.resume_auto_post', 'Resume Auto Post')
                ) : (
                  <>
                    {translate('auto_post.pause_auto_post', 'Pause Auto Post')}
                    <span
                      style={{
                        display: 'inline-block',
                        marginLeft: 14,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, #ff9800 60%, #fff176 100%)',
                        boxShadow: '0 0 18px 6px #ffb300',
                        animation: 'beepDot 1s infinite',
                        verticalAlign: 'middle',
                        border: '2.5px solid #fff3e0',
                      }}
                      className="beep-dot"
                    ></span>
                  </>
                )}
              </Button>
              {autoPostBrandDetails?.is_autopost_setup_complete &&
                (isEditing ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateClick}
                    sx={{ height: 48, minWidth: 150, fontWeight: 600 }}
                  >
                    {translate('auto_post.update_auto_post', 'Update Auto Post')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEditClick}
                    sx={{ height: 48, minWidth: 150, fontWeight: 600 }}
                  >
                    {translate('auto_post.edit_auto_post', 'Edit Auto Post')}
                  </Button>
                ))}
              {autoPostBrandDetails?.is_autopost_setup_complete && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteClick}
                  sx={{ height: 48, minWidth: 56, p: 0 }}
                >
                  <span
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }}
                  >
                    <Delete color="error" fontSize="medium" />
                  </span>
                </Button>
              )}
              {/* Note about scheduled runs */}
              {autoPostBrandDetails?.is_autopost_setup_complete && (
                <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, ml: 2, alignSelf: 'center' }}>
                  {translate('auto_post.scheduled_runs_note', 'Note: Scheduled runs occur at 12 PM UTC.')}
                </Typography>
              )}
              {(!isCustomDomain || isPortalAdmin) && (
                <Button
                  variant="outlined"
                  startIcon={<YouTubeIcon />}
                  onClick={() => window.open('https://www.youtube.com/watch?v=BczFNnvK2Us', '_blank')}
                  color="primary"
                >
                  {translate('common.tutorial')}
                </Button>
              )}
            </Box>
            {/* Generate Content Section with true disable overlay */}
            <Box sx={{ position: 'relative', mb: 4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  opacity: contentDisabled ? 0.8 : 1,
                  pointerEvents: contentDisabled ? 'none' : 'auto',
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, mb: 2.5, color: '#1976d2' }}
                >
                  <AutoAwesomeIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                  {translate('auto_post.generate_content', 'Generate Content')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={translate('auto_post.posts_per_week', 'Number of Posts per week')}
                      type="number"
                      value={semiPostsPerWeek}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, ''); // Remove non-digits
                        const num = value === '' ? '' : parseInt(value, 10);
                        if (value === '' || (num >= 1 && num <= 50)) {
                          setSemiPostsPerWeek(num);
                        }
                      }}
                      inputProps={{ min: 1, max: 50 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title={translate(
                                'auto_post.total_posts_tooltip',
                                'Total posts to generate in this schedule'
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      disabled={contentDisabled}
                    />
                    <FormHelperText>
                      {translate('auto_post.posts_per_week_helper', 'Between 1 and 50 posts')}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={contentDisabled}>
                      <InputLabel id="content-types-label-top">
                        {translate('auto_post.content_types', 'Content Types')}
                      </InputLabel>
                      <Select
                        labelId="content-types-label-top"
                        multiple
                        value={contentTypes}
                        onChange={handleContentTypeChange}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                          </Box>
                        )}
                        label={translate('auto_post.content_types', 'Content Types')}
                        disabled={contentDisabled}
                      >
                        {contentOptions.map((option) => (
                          <MenuItem
                            key={option.label}
                            value={option.label}
                            disabled={contentDisabled || option.disabled}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <span>{option.label}</span>
                              <Typography variant="caption" color="text.secondary">
                                {option.info}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {translate('auto_post.select_content_types', 'Select content types')}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <FormControl fullWidth disabled={contentDisabled || imageModels.length === 0} sx={{ mb: 2 }}>
                      <InputLabel id="image-model-label">
                        {translate('auto_post.image_model', 'Image Model')}
                      </InputLabel>
                      <Select
                        labelId="image-model-label"
                        value={selectedImageModel ? selectedImageModel.model : ''}
                        label={translate('auto_post.image_model', 'Image Model')}
                        onChange={(e) => {
                          const model = imageModels.find((m) => m.model === e.target.value);
                          setSelectedImageModel(model);
                        }}
                      >
                        {imageModels.map((modelInfo) => (
                          <MenuItem key={modelInfo.model + modelInfo.model_provider} value={modelInfo.model}>
                            {modelInfo.display_text}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {translate('auto_post.select_image_model', 'Select the image generation model')}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={translate('auto_post.prompt', 'Prompt')}
                      value={promptInput}
                      onChange={(e) => setPromptInput(e.target.value)}
                      onKeyDown={handlePromptAdd}
                      placeholder={translate(
                        'auto_post.prompt_placeholder',
                        'Press Enter to add prompt, or comma to add multiple'
                      )}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title={translate(
                                'auto_post.add_prompts_tooltip',
                                'Add prompts to guide content generation'
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 1, fontWeight: 500, fontSize: '1.1rem' }}
                      disabled={contentDisabled}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {prompt.map((p) => (
                        <Chip
                          key={p}
                          label={p}
                          onDelete={handlePromptDelete(p)}
                          deleteIcon={<CloseIcon />}
                          disabled={contentDisabled}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={translate('auto_post.website_urls', 'Website URLs')}
                      value={websiteInput}
                      onChange={(e) => setWebsiteInput(e.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && websiteInput.trim()) {
                          const newUrls = websiteInput
                            .split(',')
                            .map((k) => k.trim())
                            .filter((k) => k && isValidHttpsUrl(k) && !websiteUrls.includes(k));
                          setWebsiteUrls([...websiteUrls, ...newUrls]);
                          setWebsiteInput('');
                        }
                      }}
                      placeholder={translate(
                        'auto_post.website_urls_placeholder',
                        'Press Enter to add valid https URL, or comma to add multiple'
                      )}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title={translate(
                                'auto_post.add_website_urls_tooltip',
                                'Add website URLs for reference or content inspiration (must be https)'
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      error={
                        Boolean(websiteInput) &&
                        websiteInput.split(',').some((k) => k.trim() && !isValidHttpsUrl(k.trim()))
                      }
                      helperText={
                        Boolean(websiteInput) &&
                        websiteInput.split(',').some((k) => k.trim() && !isValidHttpsUrl(k.trim()))
                          ? translate('auto_post.invalid_https_urls', 'All URLs must be valid https URLs.')
                          : translate(
                              'auto_post.press_enter_to_add',
                              'Press Enter to add. Added URLs will appear as chips above.'
                            )
                      }
                      sx={{ mb: 1, fontWeight: 500, fontSize: '1.1rem' }}
                      disabled={contentDisabled}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {websiteUrls.map((url) => (
                        <Chip
                          key={url}
                          label={url}
                          onDelete={() => setWebsiteUrls(websiteUrls.filter((u) => u !== url))}
                          deleteIcon={<CloseIcon />}
                          disabled={contentDisabled}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <ContentFocusSelector
                      value={contentFocus}
                      onChange={setContentFocus}
                      focusOptions={focusOptions}
                      disabled={contentDisabled}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mt: 3 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={aiDeterminedHours}
                            onChange={handleAiHoursToggle}
                            color="primary"
                            disabled={contentDisabled}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {translate('auto_post.let_ai_determine_times', 'Let AI determine optimal posting times')}
                            <Tooltip
                              title={translate(
                                'auto_post.ai_analysis_tooltip',
                                'Our AI will analyze your audience and choose the best times to post'
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
                            </Tooltip>
                          </Box>
                        }
                        sx={{ mb: 1 }}
                        disabled={contentDisabled}
                      />
                      {!aiDeterminedHours && (
                        <Box>
                          <Typography gutterBottom sx={{ mb: 2 }}>
                            {translate('auto_post.preferred_posting_hours', 'Preferred Posting Hours')}
                            <Tooltip
                              title={translate(
                                'auto_post.max_engagement_tooltip',
                                "We'll aim to post during these hours for maximum engagement"
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
                            </Tooltip>
                          </Typography>
                          <Slider
                            value={postingHours}
                            onChange={(_, newValue) => setPostingHours(newValue)}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value}:00`}
                            min={0}
                            max={24}
                            step={1}
                            marks={[
                              { value: 0, label: '12AM' },
                              { value: 6, label: '6AM' },
                              { value: 12, label: '12PM' },
                              { value: 18, label: '6PM' },
                              { value: 24, label: '12AM' },
                            ]}
                            sx={{ mt: 3, width: '95%', mb: 3, mx: 2 }}
                            disabled={contentDisabled}
                          />
                          <FormHelperText>
                            {translate('auto_post.current_range', 'Current range: {start}:00 - {end}:00', {
                              start: postingHours[0],
                              end: postingHours[1],
                            })}
                          </FormHelperText>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                {/* Overlay when disabled */}
                {contentDisabled && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'auto',
                      borderRadius: 3,
                    }}
                  />
                )}
              </Paper>
            </Box>
            {/* Content Preferences Section with true disable overlay */}
            <Box sx={{ position: 'relative', mb: 4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  mb: 0,
                  opacity: contentDisabled ? 0.8 : 1,
                  pointerEvents: contentDisabled ? 'none' : 'auto',
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, mb: 2.5, color: '#1976d2' }}
                >
                  <PostAddIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                  {translate('auto_post.content_preferences', 'Content Preferences')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={translate('auto_post.content_languages', 'Content Languages')}
                      value={languageInput}
                      onChange={(e) => setLanguageInput(e.target.value)}
                      onKeyDown={handleLanguageAdd}
                      placeholder={translate('auto_post.press_enter_to_add_language', 'Press Enter to add language')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title={translate(
                                'auto_post.specify_languages_tooltip',
                                'Specify languages for generated content'
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 1 }}
                      disabled={contentDisabled}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {contentLanguages.map((language) => (
                        <Chip
                          key={language}
                          label={language}
                          onDelete={handleLanguageDelete(language)}
                          deleteIcon={<CloseIcon />}
                          disabled={contentDisabled}
                        />
                      ))}
                    </Box>
                    <FormHelperText sx={{ mb: 2 }}>
                      {translate('auto_post.added_languages_note', 'Added languages will appear as chips above')}
                    </FormHelperText>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                      {translate('auto_post.content_options', 'Content Options')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeTrending}
                            onChange={() => setIncludeTrending(!includeTrending)}
                            color="primary"
                            disabled={contentDisabled}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            {translate('auto_post.trending_topics', 'Trending Topics')}
                          </Box>
                        }
                        disabled={contentDisabled}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeHashtags}
                            onChange={() => setIncludeHashtags(!includeHashtags)}
                            color="primary"
                            disabled={contentDisabled}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TagIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            {translate('auto_post.hashtags', 'Hashtags')}
                          </Box>
                        }
                        disabled={contentDisabled}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useBrandDetails}
                            onChange={() => setUseBrandDetails(!useBrandDetails)}
                            color="primary"
                            disabled={contentDisabled}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <SettingsIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            {translate('auto_post.brand_details', 'Brand Details')}
                          </Box>
                        }
                        disabled={contentDisabled}
                      />
                      <FormControlLabel
                        control={<Switch checked={false} disabled color="primary" />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {translate(
                              'auto_post.ai_engagement_upcoming',
                              'AI Engagement Comment & Message (Upcoming)'
                            )}
                            <Tooltip
                              title={translate(
                                'auto_post.ai_engagement_tooltip',
                                'Coming soon: AI-powered comment/message replies'
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
                            </Tooltip>
                          </Box>
                        }
                        disabled
                      />
                    </Box>
                  </Grid>
                </Grid>
                {/* Overlay when disabled */}
                {contentDisabled && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'auto',
                      borderRadius: 3,
                    }}
                  />
                )}
              </Paper>
            </Box>
            {/* Location-based Content Section with true disable overlay */}
            <Box sx={{ position: 'relative', mb: 4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  mb: 0,
                  opacity: contentDisabled ? 0.8 : 1,
                  pointerEvents: contentDisabled ? 'none' : 'auto',
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, mb: 2.5, color: '#1976d2' }}
                >
                  <LocationOnIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                  {translate('auto_post.location_based_content', 'Location-based Content')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={locationBasedEnabled}
                      onChange={handleLocationToggle}
                      color="primary"
                      disabled={contentDisabled}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {translate('auto_post.enable_location_based_content', 'Enable location-based content')}
                      <Tooltip
                        title={translate(
                          'auto_post.enable_location_based_content_tooltip',
                          'Enable to generate content specific to certain locations'
                        )}
                      >
                        <InfoIcon color="action" fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                  disabled={contentDisabled}
                />
                {locationBasedEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label={translate('auto_post.add_locations', 'Add Locations')}
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      onKeyDown={handleLocationAdd}
                      placeholder={translate('auto_post.press_enter_to_add_location', 'Press Enter to add location')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title={translate(
                                'auto_post.add_locations_tooltip',
                                'Add locations for localized content'
                              )}
                            >
                              <InfoIcon color="action" fontSize="small" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      disabled={contentDisabled}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                      {locations.map((location) => (
                        <Chip
                          key={location}
                          label={location}
                          onDelete={handleLocationDelete(location)}
                          deleteIcon={<CloseIcon />}
                          disabled={contentDisabled}
                        />
                      ))}
                    </Box>
                    <FormHelperText>
                      {translate('auto_post.added_locations_note', 'Added locations will appear as chips above')}
                    </FormHelperText>
                  </Box>
                )}
                {/* Overlay when disabled */}
                {contentDisabled && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'auto',
                      borderRadius: 3,
                    }}
                  />
                )}
              </Paper>
            </Box>
            <Dialog open={confirmation.open} onClose={handleCancel}>
              <DialogTitle>{confirmation.title}</DialogTitle>
              <DialogContent>
                <DialogContentText>{confirmation.content}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancel} color="primary">
                  {translate('auto_post.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleConfirm} color="primary" autoFocus>
                  {translate('auto_post.confirm', 'Confirm')}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      }}
    </AutoPostApiHandler>
  );
};

// Remove duplicate or conflicting style variable declaration
(function addBeepDotStyle() {
  if (!document.getElementById('beep-dot-style')) {
    const beepDotStyle = document.createElement('style');
    beepDotStyle.id = 'beep-dot-style';
    beepDotStyle.innerHTML = `
    @keyframes beepDot {
      0%, 100% { opacity: 1; filter: brightness(1.1); }
      50% { opacity: 0.3; filter: brightness(0.7); }
    }
    `;
    document.head.appendChild(beepDotStyle);
  }
})();

export default FullyAutomaticTab;
